/**
 * Collaboration pricing — the arithmetic, on its own.
 *
 * Ported line-for-line from the Apps Script tool "Costing Tool for SOP 2" (project 10DMZlju…). It is a
 * separate module from the screen for one reason: these are the rules from the Costing Guideline, and
 * they have to be readable by somebody checking them against the guideline without reading React.
 *
 * Every rate and threshold below came from the original's own code and carries the guideline page it
 * cites. Where the original had a rule with a soft edge — the sub-60-day lead time — the edge is kept,
 * flagged, and surfaced rather than rounded away.
 */

/* ---------------------------------------------------------------------------------------------
 * MODE A — General Collab (OEM / ODM). No royalty. Markup Base -> T1 -> MSRP.
 * ------------------------------------------------------------------------------------------- */

/**
 * MOQ surcharge rate by order quantity (Costing Guideline p.15).
 * 500 pcs and over carries no surcharge; below that the shortfall is charged as a share of the base.
 * This is the guideline's ladder, not necessarily the rate charged — see `moqRateOf`.
 */
export const moqRate = (qty) => {
  const q = Number(qty) || 0;
  if (q <= 0) return 0;
  /* THE FIRST BAND ENDS AT 100, not 99 — the vendor's own price table, tabled at the meeting on
     2026-09-17: 1–100 EA 100%, 101–299 60%, 300–499 30%, over 500 none. This line said 99, so an
     order of exactly 100 EA was charged 60% where the table says 100%. */
  if (q <= 100) return 1.00;
  if (q <= 299) return 0.60;
  if (q <= 499) return 0.30;
  return 0;
};

/**
 * The rate actually charged. The ladder above is the guideline's answer and stays the default, but
 * the rate belongs to the vendor's own quote: TS's Natal Design sheet prices 100-299 EA at 60% and
 * 300-499 at 30%, which happens to agree, and the next sheet will not (Max, 2026-09-11). A typed
 * rate governs this SKU and nothing else.
 *
 * Blank means "use the ladder". 0 is a real answer — a vendor waiving the surcharge on a short run —
 * so it has to stay distinguishable from blank, which is why this tests the string rather than
 * truthiness.
 */
export const moqRateOf = (v) => {
  const typed = v?.moqRatePct;
  if (typed === undefined || typed === null || String(typed).trim() === '') return moqRate(v?.qty);
  return num(typed) / 100;
};

/** Lead time in days = requested XF date - PO issue date. Null unless both dates are present. */
export const leadDays = (poDate, xfDate) => {
  if (!poDate || !xfDate) return null;
  const d1 = new Date(`${poDate}T00:00:00`);
  const d2 = new Date(`${xfDate}T00:00:00`);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;
  return Math.round((d2 - d1) / 86400000);
};

/**
 * Short-lead surcharge rate (Costing Guideline p.21). 75 days or more is free; 65-74 is 30%;
 * 60-64 is 50%.
 *
 * UNDER 60 DAYS IS NOT IN THE GUIDELINE. The original applies 50% and shows a warning, because the
 * real answer is a negotiation rather than a number. That behaviour is kept exactly, and
 * `slBelowGuideline` is what the screen uses to say so out loud.
 */
export const slRate = (days) => {
  if (days === null || days === undefined) return 0;
  if (days >= 75) return 0;
  if (days >= 65) return 0.30;
  return 0.50;
};
export const slBelowGuideline = (days) => days !== null && days !== undefined && days < 60;

/**
 * Frame profit is 5% of the frame, fixed — Jay, 2026-09-04: "Frame profit is 5% fixed value". It was
 * a rate anyone could type; the field still shows it, but the number comes from here and a quote
 * saved at the old 10% recomputes at 5% when it is reopened.
 */
/* THE PARTNER PRICE IS HALF THE MSRP — Jay, 2026-09-28: "Partner price $72.57 is 60% of MSRP; our
   rule is 50%", and Max chose 50 the same day, to be revisited only if Jay says otherwise.

   A CONSTANT, not the per-quote `partnerRatio` it replaces. That field came off the form and the
   stored value was left behind, so it had stopped being anybody's choice: measured on the 13 live
   quotes, ELEVEN priced the partner at $0.00 because the key was never set and `num('')` is zero —
   CP-000073 and CP-000075 among them, both carrying a real MSRP of $211.59. The two that showed a
   figure were sitting on the 60 that arrived with the Apps Script port, which nobody ever typed.

   So every quote, old and new, now prices the partner at half its MSRP and there is nothing to
   migrate. It also drops the 40 Amy typed on CP-000011 (a Draft) — a company rule cannot be half a
   rule, and if that quote wants its own number the field has to come back to the form first. */
/* THE RULES A QUOTE IS PRICED UNDER — Max, 2026-09-29. An Admin may change these on the Settings
   tab with a reason, and "các phiếu tạo sau đó sẽ apply theo rule mà admin set": a quote resolves
   them by its OWN creation time, so changing one never reprices work already done.

   The constants below stay as the FALLBACK, not as the definition. A screen that has not been told
   the rules still prices — which matters because the calculator computes on every keystroke while
   the rules arrive over the network — and a key nobody has ever overridden has no row to read.

   Keys are the wire's, snake_case, on purpose: they come straight off `/api/pricing/rules` and a
   second spelling here is one more place the two can drift. */
export const ruleSet = (rules) => ({
  partner_ratio_pct: Number.isFinite(Number(rules?.partner_ratio_pct))
    ? Number(rules.partner_ratio_pct) : PARTNER_RATIO_PCT,
  frame_profit_pct: Number.isFinite(Number(rules?.frame_profit_pct))
    ? Number(rules.frame_profit_pct) : FRAME_PROFIT_RATE,
  tier_oh_pct: Number.isFinite(Number(rules?.tier_oh_pct))
    ? Number(rules.tier_oh_pct) : TIER_OH_PCT,
  cbd_oh_pct: Number.isFinite(Number(rules?.cbd_oh_pct))
    ? Number(rules.cbd_oh_pct) : CBD_OH_PCT,
  cbd_t2_markup_pct: Number.isFinite(Number(rules?.cbd_t2_markup_pct))
    ? Number(rules.cbd_t2_markup_pct) : CBD_T2_MARKUP_PCT,
});

/* ---------------------------------------------------------------------------------------------
 * THE CBD METHOD — HEVN Costing's own workbook (Linda Pham, "27SS Venice M'Art x HX MS CBD
 * 20261022.xlsx", 2026-10-26; Max: "update theo file của linda trước nhé").
 *
 * A SECOND WAY TO PRICE A QUOTE, chosen per quote (`pricingMethod`), not a replacement: every quote
 * saved before it was priced on the guideline above, and switching them would reprice work already
 * shown to somebody. The workbook's own rules, cell for cell:
 *
 *   Base price      = ROUND(RM + labour + NFC + vendor OH + vendor margin, 2) + handling + transport
 *                     ('Chair One'!W19) — the portal's Skin + CMT + Vendor OH&P, then the two fees.
 *   MOQ surcharge   = ROUND(Base × tier rate, 2)           (Offer Sheet J — the frame is not in it)
 *   Short lead      = ROUND(labour × 50%, 2) when short    (Offer Sheet K)
 *   Factory (T0)    = Base + Frame + MOQ + Short lead      (Offer Sheet M)
 *   HPAC Overhead   = ROUND((Base + Frame) × 30%, 2)       (Calculation F — to the cent since 2026-10-29)
 *   T1 HPAC-Entity  = ROUND(Base + Frame + O.H + surcharges, 2)            (Calculation J — likewise)
 *   T2 HPAC-Partner = ROUNDUP((Base + Frame + O.H) × 1.3 + surcharges, 0)  (Calculation K)
 *   GP vs factory   = T2 − (Base + Frame + MOQ + Short lead); margin = GP ÷ T2   (Calculation L, M)
 *
 * The surcharges are passed through at cost — not marked up — and either can be ABSORBED by HPAC
 * (Calculation B10, B11): it leaves both prices, while the factory still charges it, so GP falls.
 * The 30% and the 1.3 are pricing rules an Admin sets (Linda's own review point 2), resolved by
 * the quote's creation time like the others.
 * ------------------------------------------------------------------------------------------- */
export const PRICING_METHODS = ['guideline', 'cbd'];
export const CBD_OH_PCT = 30;
export const CBD_T2_MARKUP_PCT = 30;
export const isCbd = (v) => v?.pricingMethod === 'cbd';
/* THE WORKBOOK'S OWN MOQ LADDER — 'Chair One'!K8:M11, the factory's CBD: 1–99 pcs 100%, 100–299 60%,
   300–499 30%, 500 and over none. It differs from the guideline ladder above at exactly 100 pcs
   (that one is TS's table of 2026-09-17, 1–100 at 100%), and on this method the factory's CBD is
   the source, so its ladder is the one used. A typed rate still governs, as on the guideline. */
export const cbdMoqRate = (qty) => {
  const q = Number(qty) || 0;
  if (q <= 0) return 0;
  if (q <= 99) return 1.00;
  if (q <= 299) return 0.60;
  if (q <= 499) return 0.30;
  return 0;
};
/* SHORT LEAD TIME IS A YES / NO on the workbook ('Offer Sheet'!H7), charged at 50% of the CBD labour
   ('Offer Sheet'!G13). Left on Auto the portal answers it from the lead time the way the guideline
   does (under 75 days is short, 30% at 65–74, 50% below); Yes and No are the costing team's own
   answer, as the sheet has it. */
export const CBD_SL_PCT = 50;
export const cbdSlRate = (flag, lead) => {
  if (flag === 'yes') return CBD_SL_PCT / 100;
  if (flag === 'no') return 0;
  return slRate(lead);
};
const r2 = (x) => Math.round((Number(x) + Number.EPSILON) * 100) / 100;
// Excel's ROUNDUP(x, 0). The tolerance keeps a sum that is a whole dollar in decimal (63.0000000001
// after binary arithmetic) from being pushed to the next one.
const up0 = (x) => Math.ceil(Number(x) - 1e-9);

export const PARTNER_RATIO_PCT = 50;
export const FRAME_PROFIT_RATE = 5;

/**
 * How the MATERIAL reaches the vendor — a separate question from how the frame does, and the reason
 * this exists (Max, 2026-09-11).
 *
 *   vendor   the vendor buys its own fabric, case and packaging. The norm, and what every quote
 *            saved before this default says.
 *   ff       Helinox furnishes it free. The material itself then costs the vendor nothing — TS's
 *            Cost Breakdown lists the fabric and the woven labels at zero — but the vendor still
 *            charges for cutting and sewing what it did not buy. TS calls that the "FF CMT Fee",
 *            $0.20 a piece on the Natal Design chair.
 *
 * It is NOT the guideline's FM fee. That one is $0.04 and belongs to a furnished FRAME (p.13, p.20);
 * it sits inside the markup base and is surcharged with it. This is the vendor's own labour charge
 * on furnished MATERIAL, it is quoted rather than derived, and it lands outside the markup base and
 * outside the MOQ surcharge — which is where the offer puts it:
 *
 *     'Natal x Helinox Chair'!P11 = SUBTOTAL(P7:P10)
 *                                 = frame 17.14 + handling 0.50 + FF CMT 0.20 + transport 1.08
 *     'Natal x Helinox Chair'!L9  = SUM(P16:P22) * (1 + 60%) + P11
 *
 * The two arrangements genuinely coexist: on that chair the frame is SOLD (so handling and the 5%
 * frame profit both apply) while the fabric is furnished, which one supply switch could not say.
 */
export const MATERIAL_SUPPLY = ['vendor', 'ff'];
export const isFurnishedMaterial = (v) => v?.matSupply === 'ff';

/**
 * The items the margin is built on. Nothing else belongs here — every other cost is "excluded" and is
 * recovered as an HPAC marketing debit instead of being marked up.
 *
 * The FM fee joined them on 2026-09-04 (Jay: "FM Fee will be included in the Base Cost and removed
 * from EXCLUDED ITEMS"), which is why it is a parameter rather than another `v.` lookup: it is an
 * auto value with an override, so only computeSku knows which of the two applies.
 */
export const markupBase = (v, fmfee = 0) =>
  num(v.skin) + num(v.cmt) + num(v.ohp) + num(v.frame) + num(fmfee);

/**
 * What the MOQ surcharge is charged against — stated as the list of components it covers, rather than
 * as one flag for the one component people happened to argue about first.
 *
 * It began as `moqExcludeFrame`, a checkbox, because the frame was the only part anyone had needed to
 * take out. Then TS's own offer sheet for Natal Design turned out to surcharge
 * CMT + skin/case/pkg + OH&margin and add the frame, the handling fee and the transport AFTERWARDS,
 * untouched (Max, 2026-09-11) — and the next vendor will draw the line somewhere else again. A named
 * list says which convention a quote is on and can express one nobody has met yet; a boolean can only
 * answer the question it was born asking.
 *
 * The fixed fees (handling, transport) were never in it and still are not: they are excluded items,
 * outside the base by construction.
 */
/* HELINOX'S OVERHEAD IS FIXED AT 15% — the whiteboard says "(Fixed)" beside it and Max confirmed
   it on 2026-09-18: "O.H 15% => fix cứng". It is not a negotiating position, so it is not a box
   somebody types in; the profit above it is the adjustable half. Exported because the screen draws
   it and this file prices with it, and two copies of a constant is how they come to differ. */
export const TIER_OH_PCT = 15;

/* ADDITIONAL MATERIAL IS A LIST — Max, 2026-09-18: "user có thể click add để thêm nhiều loại phí
   khác vào á". One box could only ever hold a total, and a total is what the approver asks about:
   $4.20 of what? Each line carries what it is beside what it costs.

   `addMat` stays the number everything downstream reads, derived from the lines. A quote saved
   before the list has no lines and keeps its own figure, so nothing already priced moves. */
export const addMatTotal = (v) => {
  const lines = Array.isArray(v?.addMatLines) ? v.addMatLines : null;
  if (!lines) return num(v?.addMat);
  return lines.reduce((a, l) => a + num(l && l.amt), 0);
};

export const MOQ_PARTS = ['skin', 'cmt', 'ohp', 'frame', 'fmfee'];
/* THE FRAME IS OUT OF THE DEFAULT — Max, 2026-09-18: "MOQ surcharge ko tính frame". The frame is
   bought somewhere else and priced off its own list; surcharging a small order for it charges the
   partner a premium on a part the vendor never had to set up for. It stays in MOQ_PARTS because a
   vendor who does surcharge it can still be expressed — this changes what a NEW quote starts on,
   and every quote already saved carries its own list and is untouched. */
export const MOQ_PARTS_DEFAULT = MOQ_PARTS.filter((k) => k !== 'frame');

/* A quote saved before the list existed carries the old boolean, and has to keep computing to the
   same number: unticked meant all five, ticked meant all five but the frame. */
export const moqPartsOf = (v) => {
  if (Array.isArray(v?.moqParts)) return v.moqParts.filter((k) => MOQ_PARTS.includes(k));
  return v?.moqExcludeFrame ? MOQ_PARTS.filter((k) => k !== 'frame') : MOQ_PARTS_DEFAULT;
};

export const moqBase = (v, _legacyExcludeFrame, fmfee = 0) => {
  const parts = moqPartsOf(v);
  return parts.reduce((sum, key) => sum + (key === 'fmfee' ? num(fmfee) : num(v[key])), 0);
};

/**
 * How the frame reaches the vendor decides which fee applies, and the two are never charged together.
 *   sell (p.19)  Helinox sells the frame to the vendor. It lands in the vendor's cost, so Helinox
 *                takes a frame profit, plus Handling $0.50 and the actual freight.
 *   foc  (p.13, p.20 case 2)  The frame is furnished free. It is not in the vendor's cost, so there
 *                is no frame profit and no handling — only the FM fee of $0.04.
 */
export const autoValues = (v, supply, lead, fmfee, rules) => {
  const R = ruleSet(rules);
  const foc = supply === 'foc';
  const mr = moqRateOf(v);
  const fm = fmfee === undefined ? (foc ? 0.04 : 0) : num(fmfee);
  return {
    fprofit: foc ? 0 : num(v.frame) * R.frame_profit_pct / 100,
    handling: foc ? 0 : 0.50,
    fmfee: foc ? 0.04 : 0,
    transport: num(v.transport),          // actual freight — never guessed
    // The FM fee is inside this base now, because it is inside the markup base.
    moq: moqBase(v, undefined, fm) * mr,
    /* THE MOQ UPLIFT IS NOT IN HERE — Max, 2026-09-23: "ko có vụ (1+100%) nha chỉ cần lấy Vendor
       CMT x phần trăm thui". This was reading the guideline's "labor cost after MOQ surcharge" as
       the labour ALREADY raised by the MOQ rate, which charged the same uplift twice: `moq` above
       is the MOQ surcharge on a base that already contains the CMT, so a 100% MOQ rate was landing
       on this labour a second time. It is the vendor's labour times the short-lead rate, full stop.
       On CP-000087 that is $2.98 -> $1.49. */
    shortLead: num(v.cmt) * slRate(lead),
  };
};

/**
 * One SKU, priced. `auto` fills the seven excluded items from the rules above unless a field has been
 * overridden by hand — the same "auto with per-field override" the original screen has, because the
 * guideline is the default and a negotiated number is the exception.
 */
export const computeSku = (sku, rules) => {
  if (isCbd(sku?.values)) return computeCbd(sku, rules);
  const R = ruleSet(rules);
  const v = sku.values || {};
  const lead = leadDays(v.poDate, v.xfDate);
  const ov = sku.overrides || {};
  const useAuto = sku.auto !== false;
  const supply = v.supply || 'sell';

  /* Twice, because the FM fee is now part of the base and the MOQ surcharge is a share of that base:
     the first pass says what the fee is, the second charges the MOQ against a base that includes it.
     A typed-over FM fee therefore moves the MOQ surcharge too, which is what "included in the Base
     Cost" means. */
  const first = autoValues(v, supply, lead, undefined, R);
  const fmfee = (useAuto && !ov.fmfee) ? first.fmfee : num(v.fmfee);
  const auto = autoValues(v, supply, lead, fmfee, R);
  const pick = (k) => ((useAuto && !ov[k]) ? auto[k] : num(v[k]));

  const base = markupBase(v, fmfee);
  const items = {
    fprofit: pick('fprofit'),
    handling: pick('handling'),
    transport: pick('transport'),
    moq: pick('moq'),
    addMat: addMatTotal(v),
    /* CHARGED WHENEVER IT IS QUOTED, 2026-09-17. The "how does the material reach the vendor"
       radio only ever decided whether this one box was live, so the box IS the answer and the
       question came off the screen: leave it at 0 when the vendor buys its own material. Still kept
       off the markup base and off the MOQ base, which is what MATERIAL_SUPPLY was really about. */
    ffcmt: num(v.ffcmt),
    shortLead: pick('shortLead'),
  };
  /* An option's dropped lines, zeroed at the source so every total below counts the same set. */
  const extraOff = Array.isArray(v.extraOff) ? v.extraOff : [];
  extraOff.forEach((k) => { if (k in items) items[k] = 0; });
  const excl = Object.values(items).reduce((a, b) => a + b, 0);
  const qty = num(v.qty);
  /* A TIER RATIO IS TWO THINGS — the whiteboard, 2026-09-18: "(T2) Helinox O.H 15% (Fixed)" over
     "(T3) Helinox profit 10% (adjustable)", and 15 + 10 is the 25% the T1 box has been carrying
     all along. So this SPLITS a figure that was already there rather than changing the sum: the
     price is still basis ÷ (1 − ratio) and every quote saved before today prices to the same cent.
     A quote with NEITHER part filled in is one of those, and its whole ratio is read as profit —
     cutting it 15/10 after the fact would be the portal inventing an overhead nobody typed, and
     0/100 at least says only what the row already said.

     THE PROFIT HALF IS WHAT DECIDES WHETHER THERE IS A TIER AT ALL. The overhead is fixed at 15%
     and stands in its box whether or not this quote has such a tier, so reading "a part is filled
     in" off it would price a T1 nobody had asked for at 15%. A tier begins when somebody states
     its profit. */
  const tierParts = (total, oh, profit) => {
    const stated = String(profit ?? '').trim() !== '';
    const r = stated ? (num(oh) + num(profit)) / 100 : num(total) / 100;
    return { r, oh: stated ? num(oh) / 100 : 0, profit: stated ? num(profit) / 100 : r };
  };
  const t1p = tierParts(v.t1Ratio, v.t1Oh, v.t1Profit);
  const t0p = tierParts(v.t0Ratio, v.t0Oh, v.t0Profit);
  const t1r = t1p.r;
  const t0r = t0p.r;
  const buying = base + excl;
  /* WHAT the margin is taken on. Two costing conventions are in use and they are not the same sum:
       'base'    Jay's guideline — the margin is built on the Markup Base alone, and every excluded
                 item is recovered from the partner as an HPAC marketing debit instead of being
                 marked up. This is the default, and every quote saved before 2026-09-10 is on it.
       'buying'  the Quote Sheet — the margin is taken on the whole buying price, testing fee and
                 surcharges included, so nothing is debited separately.
     Measured on CP-000010: a $1.80 testing fee is worth $0.90 a unit at a 25% ratio depending on
     which of the two you are on, which is why this is a stated choice and not a default. */
  const onBuying = v.marginBasis === 'buying';
  const marginOn = onBuying ? buying : base;
  /* The collab premium is a PERCENTAGE of the inline RRP (Jay, 2026-09-04: "Collab Premium should be
     % not $"). It used to be a dollar amount added on top, so a quote saved with 12 in the box meant
     $12 and now means 12%. */
  const msrp = num(v.inlineRrp) * (1 + num(v.premium) / 100);

  /* THE THREE GROUPS THE STATEMENT READS IN — the meeting, 2026-09-17. The frame is bought
     somewhere else, priced off its own list, and the way it reaches the vendor decides three more
     figures; grouping it with the fabric and the sewing hid all of that. This is how the cost is
     PRESENTED and not what the margin is taken on: `markupBase` is still factory + frame together,
     unchanged, and the two-convention table says which sum each price used. */
  const factoryCost = num(v.skin) + num(v.cmt) + num(v.ohp);
  const frameCost = num(v.frame) + fmfee;
  /* WHAT EACH TIER EARNS (Max, 2026-09-17: "profit theo T1 và T2"). A tier IS a margin on a basis,
     so its profit is the tier price less the sum that margin was taken on. The subtraction is the
     same on both conventions and neither double-counts: on the guideline the others cost comes back
     from the partner as the HPAC debit, on the quote sheet it is already inside the price.
     A tier with no ratio has NO profit line rather than a zero — a zero would read as "this tier
     earns nothing", which is a different statement from "this quote has no such tier". */
  /* `basis ÷ (1 − ratio)` used to live here. It is GONE rather than kept beside the new rule: two
     formulas for one tier is what put two different T1s on one screen (Max, 2026-09-22). */
  /* And each tier's margin splits the same way it was entered. A ratio taken as basis ÷ (1 − r)
     IS r of the PRICE, so the overhead share is the tier price × the overhead percentage and the
     two parts add back to the margin exactly — no rounding is left over to explain. */
  /* THE SIMULATOR'S FOUR LINES, as Max stated them on 2026-09-22:

         Helinox O.H  = % x (Factory cost + Frame cost) + Extra cost 1
         Helinox profit = % x Helinox O.H
         Extra cost   = Extra cost 1
         T1           = O.H + profit + Extra cost

     THIS IS NOT THE TIER PRICE ABOVE and does not try to be. `t0`/`t1` are still basis / (1 - r),
     which is what the Results table, the mail and the Excel export print; these four are the
     SIMULATOR'S own statement and Max asked for them in this shape.

     EXTRA COST 1 LANDS IN THE PRICE TWICE — once inside the overhead line and again on the extra
     line. That was measured and put to him with the figures ($36.04 + $10.81 + $30.05 = $76.90 on
     CP's screen quote) and he chose this reading over the two that do not double it. Recorded here
     because it is the first thing anybody checking these numbers will notice.

     `on` is the basis the quote is on, so the guideline reads exactly as he wrote it (Factory +
     Frame) and the quote-sheet convention keeps the same shape against the buying cost. */
  /* T1, AS MAX RESTATED IT — 2026-09-24:
       Helinox O.H     15% x (Factory cost + Frame cost + Extra cost 1)
       Helinox profit  10% x (Factory cost + Frame cost + Extra cost 1)
       "TRONG ĐÓ EXTRA COST SẼ KO BAO GỒM MOQ surcharge VÀ Short-lead surcharge"

     It answers both halves of Jay's objection at once (2026-09-23): "T1 adds extra cost again, and
     Helinox profit 10% is taken on $36.04 rather than on the base". The old line built the whole
     extra cost INTO the overhead and then added it a second time on the row below, and took the
     profit on that overhead rather than on the base. Now each percentage is taken on the same
     stated figure, and the extra cost is added once, on its own line.

     THE BASIS CHOICE NO LONGER REACHES T1, which is worth knowing rather than discovering. The
     base is now named in cost blocks instead of in conventions — and Total buying cost IS factory
     + frame + the whole extra, so removing the two surcharges from it lands on exactly the same
     figure as the guideline base plus them. T2 still divides by whichever convention is chosen. */
  /* T1's BASE IS THE WHOLE EXTRA COST — Max, 2026-09-24, correcting himself: "nó bao gồm MOQ
     surcharge và short-lead surcharge, nhưng khi xuống option thì user có thể loại bỏ nó ra".

     So the two surcharges are in, and an OPTION is where any extra-cost line can be dropped:
     `extraOff` names the items that option does not apply, and they are zeroed before anything is
     summed — which takes them out of the margin base AND out of the debit, because an item this
     option does not charge is not charged anywhere. The quote itself carries no exclusions, so for
     it this changes nothing.

     `t1On` is then base + extra, which IS the total buying cost. It is kept as its own name because
     the two are the same figure for a different reason, and the day one of them moves the other
     should not follow it silently. */
  const t1On = base + excl;
  const simLine = (ohPct, pfPct) => {
    const oh = ohPct * t1On;
    const pf = pfPct * t1On;
    /* THE PRICE IS THE THREE LINES THE SCREEN SHOWS — Max, 2026-09-24, taking the Extra cost row
       out of the block and putting the base in above the overhead. It used to be
       O.H + profit + the whole extra cost; with the extra no longer a line, adding it here would
       make a column that does not add up, which is the one thing a build-up must never do.
       Nothing is lost by it: Extra cost 1 is "not marked up — recovered as an HPAC marketing
       debit", and the debit is still counted in full beside the tier. */
    return { oh, pf, extra: excl, on: t1On, price: t1On + oh + pf };
  };
  const sideOf = (on, debit) => {
    // A tier exists when its profit half is stated — the same rule `tierParts` uses above.
    const sA = t0r > 0 ? simLine(t0p.oh, t0p.profit) : null;
    /* T2 IS THE DIVISION, NOT THE BUILD-UP — Max, 2026-09-24: "cái T2 này sai rồi, tôi đang dùng
       công thức T1 áp qua nhưng thật ra nó vẫn theo công thức cũ là Total buying cost ÷ (1 − T2
       ratio) hoặc Markup Base ÷ (1 − T2 ratio)".

       This reverses part of 2026-09-22, when his T1 formula was made the tier price EVERYWHERE so
       that one tier could not carry two prices. That reasoning still holds for T1; what it got
       wrong was assuming both tiers are built the same way. They are not: T1 is what HPAC adds on
       top, line by line, and T2 is a margin taken OUT of a price — the difference between adding
       45% and dividing by 0.55.

       The database agrees with him, which is the part worth measuring rather than debating:
       CP-000087 stores t1 = 72.57036…, and markupBase 39.9137 ÷ (1 − 45%) = 72.5704 exactly, while
       the build-up gives 66.67. Every saved quote carries the division.

       The two percentage halves still define the ratio; they are now the SPLIT of what the tier
       earns, price × each half, which adds back to price − basis to the cent. */
    const p2 = t1r > 0 && t1r < 1 ? on / (1 - t1r) : null;
    const sB = p2 === null ? null
      : { oh: p2 * t1p.oh, pf: p2 * t1p.profit, extra: 0, price: p2 };
    /* ONE T1 ON THE SCREEN — Max, 2026-09-22, pointing at the comparison table: "2 cái này đã được
       apply công thức mới rùi mà". His formula was in the simulator only, so the same tier had two
       prices on one page: the simulator's O.H + profit + extra, and this table's basis ÷ (1 − r).
       Both were defensible and that is exactly the trouble — a reader had no way to know which one
       the approver is being asked for. The formula he stated is now the tier price everywhere, and
       `basis ÷ (1 − r)` is gone rather than kept as a second answer.

       WHAT THE TIER EARNS follows from it: the two Helinox lines, overhead and profit. They add to
       the row above them exactly, because that row IS their sum. Note that his overhead line
       carries the extra cost inside it, so the extra is in this figure as well as on its own line
       — the double count he was shown and chose. */
    return {
      on,
      t1: sA && sA.price,
      t2: sB && sB.price,
      debit,
      p1: sA && (sA.oh + sA.pf),
      p2: sB && (sB.oh + sB.pf),
      /* THE TIER PRICE'S OWN SPLIT, which is what the Results table prints under "Profit at T1":
         the overhead share is the tier price x the overhead percentage, and the two add back to
         the profit line above them exactly. Kept separate from the simulator's lines below —
         putting Max's formula in here made those two rows stop adding up to the row they explain,
         which is the whole reason they are indented under it. */
      oh1: sA && sA.oh,
      pf1: sA && sA.pf,
      oh2: sB && sB.oh,
      pf2: sB && sB.pf,
      // The SIMULATOR's four lines, which are Max's formula and a different statement.
      simOh1: sA && sA.oh,
      simPf1: sA && sA.pf,
      simOh2: sB && sB.oh,
      simPf2: sB && sB.pf,
      sim1: sA && sA.price,
      sim2: sB && sB.price,
    };
  };

  return {
    lead,
    slBelowGuideline: slBelowGuideline(lead),
    factoryCost,
    frameCost,
    /* Both conventions, always — the screen shows them side by side so the approver can see what
       the choice is worth rather than being told. */
    byBasis: { base: sideOf(base, excl * qty), buying: sideOf(buying, 0) },
    // What each tier's ratio is made of, as fractions, so the screen prints the split it was given.
    tierSplit: { t1: t0p, t2: t1p },
    // Both, because the screen offers the typed rate against the ladder it replaces.
    moqRate: moqRateOf(v),
    moqRateAuto: moqRate(qty),
    moqRateManual: String(v.moqRatePct ?? '').trim() !== '',
    slRate: slRate(lead),
    auto,
    items,
    fmfee,
    qty,
    markupBase: base,
    // What T1's two percentages are taken on — see simLine.
    t1On,
    extraOff,
    // What the MOQ surcharge is charged on, so the screen can show the number behind the choice.
    moqBaseAmount: moqBase(v, undefined, fmfee),
    moqParts: moqPartsOf(v),
    excluded: excl,
    buyingCost: buying,
    marginBasis: onBuying ? 'buying' : 'base',
    marginOn,
    /* Booked as a debit against the partner, so it is the per-unit figure times the whole order —
       but only on the 'base' convention. On 'buying' those items are already inside the price the
       partner pays, and charging them again would be the same money collected twice. */
    hpacDebit: onBuying ? 0 : excl * qty,
    /* T0 — the manufacturing entity's tier. The Quote Sheet prices this deal twice because HPAC is
       both the manufacturing and the selling entity ("Cost Accounting" note 1), and one ratio box
       cannot say that: on CP-000010 the T0 ratio, 8%, had been typed into the T1 box, so the number
       on screen was neither tier. Null, not zero, when no T0 ratio is set — this deal has one. */
    t0: t0r > 0 ? simLine(t0p.oh, t0p.profit).price : null,
    /* T2 — the Helinox entity's tier. A MARGIN ON the basis, not a markup added to it: at a 45%
       ratio the basis is 55% of T2.

       Null, not a number, when no ratio is set — Max, 2026-09-14: "nếu ko điền T2 ratio thì dòng T2
       (Helinox entity) bên phần results sẽ ko xuất hiện". With no ratio the formula returns the
       basis unchanged, so the row printed the Markup Base under a T2 heading, which reads as a
       price somebody worked out. Same rule the tier above it has always used. */
    /* THE DIVISION, matching the comment above it and `sideOf` beside it — Max, 2026-09-24, on a
       screenshot of the options: "cùng phần trăm nhưng khác nhau giá trị T2". This line is what the
       option cards, the Results panel, the approval mail and the Excel export all read, so while it
       still built T2 up the way T1 is built, the same tier had two prices on one screen at the same
       ratio: 65.95 in block 5 and 66.67 on the card beside it. That is the fault 2026-09-22 set out
       to remove, reintroduced by fixing only half of it. */
    t1: t1r > 0 && t1r < 1 ? marginOn / (1 - t1r) : null,
    msrp,
    partnerPrice: msrp * (R.partner_ratio_pct / 100),
    // What the four base items each contribute — the composition bars on the results card.
    composition: [
      { key: 'skin', label: 'Skin + Case + Pkg', value: num(v.skin) },
      { key: 'cmt', label: 'Vendor CMT (labor)', value: num(v.cmt) },
      { key: 'ohp', label: 'Vendor OH & Profit', value: num(v.ohp) },
      // In the order the form asks for them. Only when there is one: the FM fee is zero whenever
      // Helinox sells the frame to the vendor, and a permanently empty bar is noise on those quotes.
      ...(fmfee ? [{ key: 'fmfee', label: 'FM fee', value: fmfee }] : []),
      { key: 'frame', label: 'Frame', value: num(v.frame) },
    ].map((x) => ({ ...x, share: base > 0 ? x.value / base : 0 })),
  };
};

/* One SKU on the CBD method — see the block above `PRICING_METHODS`. It returns the SAME shape as
   the guideline's result, so every screen that already prints a quote (the Results table, the
   approver's blocks, the mail, the Excel export) prints this one without a second reading of it:
     t0           T1, HPAC → the Helinox entity
     t1           T2, HPAC → the partner — which is also `partnerPrice`, what the partner pays
     markupBase   Base + Frame          excluded   MOQ + short lead
     buyingCost   the factory price, T0 in the workbook
   and `cbd` carries the workbook's own lines for the screens that draw them. */
export const computeCbd = (sku, rules) => {
  const R = ruleSet(rules);
  const v = sku.values || {};
  const lead = leadDays(v.poDate, v.xfDate);
  const ov = sku.overrides || {};
  const useAuto = sku.auto !== false;
  const supply = v.supply || 'sell';
  const foc = supply === 'foc';
  const qty = num(v.qty);
  const pickOr = (k, autoVal) => ((useAuto && !ov[k]) ? autoVal : num(v[k]));
  const fmfee = pickOr('fmfee', foc ? 0.04 : 0);
  const handling = pickOr('handling', foc ? 0 : 0.50);
  const transport = num(v.transport);
  const addMat = addMatTotal(v);
  const ffcmt = num(v.ffcmt);
  /* The vendor's own figure is rounded to the cent BEFORE the two fees are added, as 'Chair One'!W19
     does: ROUND(SUM(W13:W16) + SUM(W17:W18), 2) + W8 + W9. */
  const vendor = r2(num(v.skin) + num(v.cmt) + num(v.ohp) + fmfee);
  const base = r2(vendor + handling + transport + addMat + ffcmt);
  const frame = num(v.frame);
  const typedMoq = String(v.moqRatePct ?? '').trim() !== '';
  const mr = typedMoq ? num(v.moqRatePct) / 100 : cbdMoqRate(qty);
  const sr = cbdSlRate(v.slFlag, lead);
  const items = {
    fprofit: 0,
    handling,
    transport,
    moq: pickOr('moq', r2(base * mr)),
    addMat,
    ffcmt,
    shortLead: pickOr('shortLead', r2(num(v.cmt) * sr)),
  };
  const extraOff = Array.isArray(v.extraOff) ? v.extraOff : [];
  extraOff.forEach((k) => { if (k === 'moq' || k === 'shortLead') items[k] = 0; });
  const moq = items.moq;
  const sl = items.shortLead;
  const absorbMoq = !!v.absorbMoq;
  const absorbSl = !!v.absorbSl;
  const factory = base + frame + moq + sl;
  /* TO THE CENT — Linda's Answer 4 workbook (2026-10-29, Max: "follow theo nguyên tắc làm tròn của file này"):
     Calculation!F6 = ROUND((Base + Frame) × 30%, 2) and J6 = ROUND(SUM(D6:I6), 2). The Venice workbook of
     26/10 had both to the dollar; on Answer 4's Cafe Table that put T2 at 93 where the sheet has 92. T2 and
     the margin options still round UP to the dollar, as both workbooks do. */
  const oh = r2((base + frame) * R.cbd_oh_pct / 100);
  const core = base + frame + oh;
  const charged = (absorbMoq ? 0 : moq) + (absorbSl ? 0 : sl);
  const t1 = r2(core + charged);
  /* A MARGIN ON THE SELLING PRICE — Tina, 2026-10-23 ("several pricing options with different margin
     rates for YL to choose from"), Max chose the margin on T2 on 2026-10-26: an option may set
     `cbdMargin`, and then T2 = vendor cost / (1 - margin), rounded up to the dollar like the workbook's
     T2. The vendor cost already carries both surcharges, so the absorb ticks do not move this price.
     Empty, zero or 95% and over: the workbook's own T2. */
  const tm = String(v.cbdMargin ?? '').trim() === '' ? 0 : num(v.cbdMargin);
  const targetMargin = tm > 0 && tm < 95 ? tm : null;
  const t2 = targetMargin !== null
    ? up0(factory / (1 - targetMargin / 100))
    : up0(core * (1 + R.cbd_t2_markup_pct / 100) + charged);
  const gp = t2 - factory;
  const msrp = num(v.inlineRrp) * (1 + num(v.premium) / 100);
  const side = {
    on: core, t1, t2, debit: 0,
    p1: t1 - factory, p2: gp,
    oh1: oh, pf1: t1 - factory - oh, oh2: oh, pf2: gp - oh,
    simOh1: oh, simPf1: t1 - factory - oh, simOh2: oh, simPf2: gp - oh, sim1: t1, sim2: t2,
  };
  return {
    method: 'cbd',
    lead,
    slBelowGuideline: slBelowGuideline(lead),
    factoryCost: num(v.skin) + num(v.cmt) + num(v.ohp),
    frameCost: frame + fmfee,
    byBasis: { base: side, buying: side },
    tierSplit: { t1: { r: 0, oh: 0, profit: 0 }, t2: { r: 0, oh: 0, profit: 0 } },
    moqRate: mr,
    moqRateAuto: cbdMoqRate(qty),
    moqRateManual: typedMoq,
    slRate: sr,
    slFlag: v.slFlag || '',
    auto: { fprofit: 0, handling: foc ? 0 : 0.50, fmfee: foc ? 0.04 : 0, transport, moq: r2(base * mr), shortLead: r2(num(v.cmt) * sr) },
    items,
    fmfee,
    qty,
    markupBase: base + frame,
    t1On: core,
    extraOff,
    moqBaseAmount: base,
    moqParts: moqPartsOf(v),
    excluded: moq + sl,
    buyingCost: factory,
    marginBasis: 'buying',
    marginOn: core,
    hpacDebit: 0,
    t0: t1,
    t1: t2,
    msrp,
    partnerPrice: t2,
    composition: [
      { key: 'skin', label: 'Skin + Case + Pkg', value: num(v.skin) },
      { key: 'cmt', label: 'Vendor CMT (labor)', value: num(v.cmt) },
      { key: 'ohp', label: 'Vendor OH & Profit', value: num(v.ohp) },
      ...(fmfee ? [{ key: 'fmfee', label: 'FM fee', value: fmfee }] : []),
      { key: 'frame', label: 'Frame', value: frame },
    ].map((x) => ({ ...x, share: base + frame > 0 ? x.value / (base + frame) : 0 })),
    cbd: {
      base, frame, moq, sl, factory, oh, ohPct: R.cbd_oh_pct, markupPct: R.cbd_t2_markup_pct,
      core, charged, t1, t2, gp, margin: t2 ? gp / t2 : null, absorbMoq, absorbSl, targetMargin,
      vendor, handling, transport, addMat, ffcmt,
    },
  };
};

/* ---------------------------------------------------------------------------------------------
 * MODE B — Licensed Collab. A royalty is paid on the partner's own selling price, so the question is
 * not "what does it cost" but "what margin is left after the royalty, in each entity's market".
 * ------------------------------------------------------------------------------------------- */

/**
 * One entity's numbers. `landed` is computed once for the project (T1 FOB plus logistics) and passed
 * in, because it is the same landed cost whichever market sells it.
 *
 * Margins are quoted PRE and POST royalty on purpose: pre-royalty is the margin the business is used
 * to seeing, and the gap between the two is the whole point of the licensed model.
 */
export const computeEntity = (ent, g, landed) => {
  const fx = num(ent.fx) || 1;
  const srpLocal = num(ent.srp);
  const srpUsd = srpLocal / fx;
  const wsPct = num(g.wholesale) / 100;
  const wsLocal = srpLocal * wsPct;
  const wsUsd = srpUsd * wsPct;
  const wsRoyLocal = wsLocal * (num(g.wsRoyalty) / 100);
  const wsRoyUsd = wsUsd * (num(g.wsRoyalty) / 100);
  const dtcRoyLocal = srpLocal * (num(g.dtcRoyalty) / 100);
  const dtcRoyUsd = srpUsd * (num(g.dtcRoyalty) / 100);
  return {
    fx,
    srpLocal,
    srpUsd,
    wsLocal,
    wsUsd,
    wsRoyLocal,
    dtcRoyLocal,
    wsMarginPre: wsUsd > 0 ? 1 - landed / wsUsd : 0,
    wsMarginPost: wsUsd > 0 ? 1 - (landed + wsRoyUsd) / wsUsd : 0,
    dtcMarginPre: srpUsd > 0 ? 1 - landed / srpUsd : 0,
    dtcMarginPost: srpUsd > 0 ? 1 - (landed + dtcRoyUsd) / srpUsd : 0,
    // How many times the FOB the shelf price is — the number the licensing side actually argues about.
    fobMultiple: num(g.t1Fob) > 0 ? srpUsd / num(g.t1Fob) : 0,
  };
};

export const landedCost = (g) => num(g.t1Fob) * (1 + num(g.logistics) / 100);

/** A margin worth flagging. The original colours these; the thresholds are its. */
export const marginTone = (m) => {
  if (m >= 0.50) return 'good';
  if (m >= 0.30) return 'ok';
  return 'bad';
};

export function num(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/* ONE ROUNDING FOR THE WHOLE SCREEN. `toLocaleString` rounds the DECIMAL value and `toFixed`
   rounds the BINARY one, so a figure landing exactly on half a cent came out two ways a few pixels
   apart: the short-lead box showed 2.48 (`calc.items.shortLead.toFixed(2)`, 4.97 x 50% = 2.485)
   while the formula hint explaining that very box printed 2.49. An explanation that disagrees with
   the figure it explains tells the reader the figure is wrong. Rounding through `toFixed` first
   makes every printed dollar agree with the input boxes, which are what people type into. */
export const fmtUSD = (v) => `$${Number((Number(v) || 0).toFixed(2)).toLocaleString('en-GB', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
export const fmtPct = (v) => `${((Number(v) || 0) * 100).toFixed(1)}%`;
export const fmtLocal = (v, fx) => (fx === 1
  ? fmtUSD(v)
  : (Number(v) || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 }));
